import { useEffect, useMemo, useRef, useState } from 'react';
import { 
  Plus, Search, List, LayoutGrid, GripVertical, 
  Edit3, Trash2, Eye, EyeOff, Save, X, 
  CheckCircle2, AlertCircle, Award, Info, Building2,
  Camera, FileText, Settings, ChevronRight, RefreshCcw, Handshake
} from 'lucide-react';
import '../../styles/admin.css';
import { fetchReferencesAdmin, createReference, updateReference, deleteReference } from '../../lib/references';
import { getFileUrl, pb } from '../../lib/pb';

export default function AdminReferences() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [mode, setMode] = useState('create');
  const [viewMode, setViewMode] = useState('grid');
  const [filterStatus, setFilterStatus] = useState('all');
  const [draggingId, setDraggingId] = useState(null);
  const [success, setSuccess] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetchReferencesAdmin(page, 100);
      let list = res.items || [];
      if (search) {
        const q = search.toLowerCase();
        list = list.filter(r => (r.name || '').toLowerCase().includes(q));
      }
      setItems(list);
      setTotalPages(res.totalPages || 1);
    } catch (e) {
      setError(e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [page, search]);

  useEffect(() => {
    let unsub = null;
    (async () => {
      try {
        unsub = await pb.collection('references').subscribe('*', () => load());
      } catch (e) { console.error(e); }
    })();
    return () => { if (unsub) pb.collection('references').unsubscribe('*'); };
  }, []);

  const filteredItems = useMemo(() => {
    return items.filter(r => {
      if (filterStatus === 'published' && !r.published) return false;
      if (filterStatus === 'draft' && r.published) return false;
      return true;
    });
  }, [items, filterStatus]);

  const handleDragStart = (id) => setDraggingId(id);

  const handleDragOver = (targetId, e) => {
    e.preventDefault();
    if (!draggingId || draggingId === targetId) return;
    setItems(prev => {
      const next = [...prev];
      const fromIdx = next.findIndex(r => r.id === draggingId);
      const toIdx = next.findIndex(r => r.id === targetId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
  };

  const persistOrder = async () => {
    if (!items.length) return;
    try {
      const updates = items.map((r, index) => {
        const newOrder = (index + 1) * 10;
        if (r.order !== newOrder) return updateReference(r.id, { order: newOrder });
        return null;
      }).filter(Boolean);
      if (updates.length) {
        await Promise.all(updates);
        setSuccess('Ordre mis à jour ✨');
        setTimeout(() => setSuccess(''), 2000);
      }
    } catch (e) { setError("Erreur de réorganisation"); }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    persistOrder();
    setDraggingId(null);
  };

  const handleTogglePublished = async (r) => {
    const oldStatus = r.published;
    setItems(prev => prev.map(item => item.id === r.id ? { ...item, published: !oldStatus } : item));

    try {
      await updateReference(r.id, { published: !oldStatus });
      setSuccess(!oldStatus ? 'Référence publiée ⭐' : 'Référence masquée 🌑');
      setTimeout(() => setSuccess(''), 2000);
    } catch (e) {
      setItems(prev => prev.map(item => item.id === r.id ? { ...item, published: oldStatus } : item));
      setError(e.message);
    }
  };

  const openCreate = () => {
    setMode('create');
    setEditing({ name: '', description: '', logo: '', published: true });
  };

  const openEdit = (r) => { setMode('edit'); setEditing(r); };

  const handleDelete = async (r) => {
    if (!window.confirm(`Supprimer la référence "${r.name}" ?`)) return;
    try {
      await deleteReference(r.id);
      setSuccess('Référence supprimée 🗑️');
      setTimeout(() => setSuccess(''), 2000);
    } catch (e) { setError(e.message); }
  };

  return (
    <div className="admin-main animation-fade-in">
      <header className="admin-header-premium">
        <div className="header-content">
          <div className="header-tag">Ils nous font confiance</div>
          <h1>Nos Références <span className="count-badge">{items.length}</span></h1>
          <p className="header-subtitle">Mettez en avant vos clients prestigieux et vos réalisations institutionnelles.</p>
        </div>
        <button className="btn-add-premium" onClick={openCreate}>
          <Plus size={20} />
          <span>Ajouter une référence</span>
        </button>
      </header>

      {success && <div className="toast-success"><CheckCircle2 size={18}/> {success}</div>}
      {error && <div className="toast-error"><AlertCircle size={18}/> {error}</div>}

      <div className="admin-controls-bar">
        <div className="search-box-premium">
          <Search size={18} className="search-icon" />
          <input 
            placeholder="Rechercher un client..." 
            value={search} 
            onChange={e => { setPage(1); setSearch(e.target.value); }}
          />
        </div>

        <div className="filters-and-view">
          <div className="capsule-select">
            <button className={filterStatus === 'all' ? 'active' : ''} onClick={() => setFilterStatus('all')}>Tous</button>
            <button className={filterStatus === 'published' ? 'active' : ''} onClick={() => setFilterStatus('published')}>Publiés</button>
            <button className={filterStatus === 'draft' ? 'active' : ''} onClick={() => setFilterStatus('draft')}>Brouillons</button>
          </div>

          <div className="view-switcher-premium">
            <button className={viewMode === 'list' ? 'active' : ''} onClick={() => setViewMode('list')}><List size={18}/></button>
            <button className={viewMode === 'grid' ? 'active' : ''} onClick={() => setViewMode('grid')}><LayoutGrid size={18}/></button>
          </div>
        </div>
      </div>

      {loading && (
        <div className="loading-state">
          <RefreshCcw className="spin" size={32} />
          <p>Récupération des avis clients...</p>
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="empty-state-card">
          <Award size={48} />
          <h3>Aucune référence pour le moment</h3>
          <p>Enregistrez vos logos clients pour construire votre mur de confiance.</p>
          <button className="btn filled" onClick={openCreate}>Créer une référence</button>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className={`references-content-container ${viewMode}`}>
          {viewMode === 'list' ? (
            <div className="premium-list">
              {filteredItems.map((r) => (
                <div 
                  key={r.id} 
                  className={`list-item-row ${draggingId === r.id ? 'dragging' : ''}`}
                  draggable
                  onDragStart={() => handleDragStart(r.id)}
                  onDragOver={(e) => handleDragOver(r.id, e)}
                  onDrop={handleDrop}
                >
                  <div className="drag-handle"><GripVertical size={18}/></div>
                  <div className="item-icon-box wide">
                    {r.logo ? (
                      <img src={getFileUrl(r, r.logo)} alt={r.name} />
                    ) : (
                      <Building2 size={20} className="placeholder" />
                    )}
                  </div>
                  <div className="item-info">
                    <div className="item-title">{r.name}</div>
                    <div className="item-meta">
                      {r.description ? r.description : "Pas de description"}
                    </div>
                  </div>
                  <div className="item-status">
                     <button className={`status-pill ${r.published ? 'online' : 'offline'}`} onClick={() => handleTogglePublished(r)}>
                        {r.published ? <Eye size={14}/> : <EyeOff size={14}/>}
                        <span>{r.published ? 'Publié' : 'Masqué'}</span>
                     </button>
                  </div>
                  <div className="item-actions">
                    <button className="action-btn edit" onClick={() => openEdit(r)} title="Modifier">
                      <Edit3 size={18}/>
                    </button>
                    <button className="action-btn delete" onClick={() => handleDelete(r)} title="Supprimer">
                      <Trash2 size={18}/>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="premium-grid-ref">
              {filteredItems.map((r) => (
                <div 
                  key={r.id} 
                  className="ref-card-premium animation-slide-up"
                  draggable
                  onDragStart={() => handleDragStart(r.id)}
                  onDragOver={(e) => handleDragOver(r.id, e)}
                  onDrop={handleDrop}
                >
                  <div className="card-media logo-ref">
                    {r.logo ? <img src={getFileUrl(r, r.logo)} alt={r.name} /> : <div className="card-placeholder"><Building2 size={40}/></div>}
                  </div>
                  <div className="card-body centered">
                    <div className="card-header centered">
                       <div className={`status-badge-mini ${r.published ? 'active' : ''}`}>{r.published ? 'En ligne' : 'Masqué'}</div>
                    </div>
                    <h3 className="card-title text-center">{r.name}</h3>
                    <p className="card-desc-mini">{r.description}</p>
                    <div className="card-actions-row">
                       <button className={`btn-mini ${r.published ? 'online' : 'offline'}`} onClick={() => handleTogglePublished(r)} title={r.published ? 'Masquer' : 'Publier'}>
                          {r.published ? <Eye size={16}/> : <EyeOff size={16}/>}
                       </button>
                       <div className="spacer"></div>
                       <button className="btn-mini edit" onClick={() => openEdit(r)} title="Modifier"><Edit3 size={16}/></button>
                       <button className="btn-mini delete" onClick={() => handleDelete(r)} title="Supprimer"><Trash2 size={16}/></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {editing && (
        <ReferenceStudio
          mode={mode}
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      <style>{`
        .premium-grid-ref { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 25px; padding: 20px 0; }
        
        .ref-card-premium { background: #fff; border-radius: 24px; border: 1px solid var(--admin-border); overflow: hidden; position: relative; transition: all 0.3s ease; cursor: grab; }
        .ref-card-premium:hover { transform: translateY(-5px); box-shadow: 0 15px 30px -10px rgba(0,0,0,0.1); border-color: var(--admin-primary); }
        
        .card-media.logo-ref { height: 140px; position: relative; background: #fff; display: flex; align-items: center; justify-content: center; padding: 30px; border-bottom: 1px solid #f8fafc; }
        .card-media.logo-ref img { width: 100%; height: 100%; object-fit: contain; filter: grayscale(0.2); transition: all 0.3s; }
        .ref-card-premium:hover .card-media.logo-ref img { filter: grayscale(0) scale(1.05); }
        
        .status-badge-mini { font-size: 9px; text-transform: uppercase; letter-spacing: 1px; font-weight: 800; color: #94a3b8; border: 1px solid #f1f5f9; padding: 2px 8px; border-radius: 99px; }
        .status-badge-mini.active { color: #10b981; border-color: #d1fae5; background: #f0fdf4; }

        .card-body.centered { padding: 18px 20px 22px 20px; display: flex; flex-direction: column; align-items: center; }
        .card-title { font-size: 16px; font-weight: 800; color: #0f172a; margin: 5px 0; }
        .card-desc-mini { font-size: 12px; color: #64748b; line-height: 1.4; text-align: center; margin-bottom: 15px; height: 34px; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }

        .item-icon-box.wide { width: 100px; height: 50px; background: #fff; padding: 8px; border: 1px solid #f1f5f9; }
        .item-icon-box.wide img { width: 100%; height: 100%; object-fit: contain; }

        @media (max-width: 600px) {
          .premium-grid-ref { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
}

function ReferenceStudio({ mode, initial, onClose, onSaved }) {
  const [activeTab, setActiveTab] = useState('identity');
  const [name, setName] = useState(initial.name || '');
  const [description, setDescription] = useState(initial.description || '');
  const [published, setPublished] = useState(initial.published ?? true);
  const [logo, setLogo] = useState(() => initial.logo ? { type: 'existing', name: initial.logo } : null);
  
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const compressImage = (file, maxWidth = 800, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        try {
          let { width, height } = img;
          if (width > maxWidth) { height = (height * maxWidth) / width; width = maxWidth; }
          canvas.width = width; canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('Compression error')), 'image/jpeg', quality);
        } catch (err) { reject(err); }
      };
      img.onerror = () => reject(new Error("Image error"));
      img.src = URL.createObjectURL(file);
    });
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const compressed = await compressImage(file);
      const finalFile = new File([compressed], file.name, { type: 'image/jpeg' });
      setLogo({ type: 'new', file: finalFile });
    } catch (err) { setError("Erreur compression logo"); }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const data = { name, description, published };
    if (logo) { if (logo.type === 'new') data.logo = logo.file; } 
    else { data.logo = null; }

    try {
      if (mode === 'create') await createReference(data);
      else await updateReference(initial.id, data);
      onSaved();
    } catch (err) { setError(err?.message || 'Erreur sauvegarde'); } 
    finally { setSaving(false); }
  };

  const previewUrl = logo ? (logo.type === 'existing' ? getFileUrl(initial, logo.name) : URL.createObjectURL(logo.file)) : '';

  return (
    <div className="admin-modal-overlay">
      <div className="admin-modal-container compact studio-reference animation-slide-up">
        <header className="modal-header-premium">
          <div className="header-info">
            <div className="header-tag">{mode === 'create' ? 'Nouveau' : 'Édition'}</div>
            <h2>Studio Référence</h2>
          </div>
          <button className="btn-close-modal" onClick={onClose}><X size={20}/></button>
        </header>

        <div className="modal-tabs">
          <button className={activeTab === 'identity' ? 'active' : ''} onClick={() => setActiveTab('identity')}>
            <Handshake size={16}/> <span>Partenariat</span>
          </button>
          <button className={activeTab === 'media' ? 'active' : ''} onClick={() => setActiveTab('media')}>
            <Camera size={16}/> <span>Logo Client</span>
          </button>
        </div>

        <form onSubmit={onSubmit} className="modal-body-premium">
           {error && <div className="modal-error"><AlertCircle size={14}/> {error}</div>}

           <div className="tab-panels">
              {activeTab === 'identity' && (
                <div className="tab-pane active animation-fade-in">
                   <div className="form-group-premium">
                      <label>Dénomination Client / Projet</label>
                      <input value={name} onChange={e => setName(e.target.value)} required placeholder="ex: Pharmacie du Centre, Auch (32)" />
                   </div>
                   <div className="form-group-premium">
                      <label>Description courte</label>
                      <textarea rows={3} value={description} onChange={e => setDescription(e.target.value)} placeholder="ex: Installation complète de panneaux solaires et isolation des combles." />
                   </div>
                   <div className="toggle-switch-group">
                      <span>Afficher sur le site public</span>
                      <label className="switch">
                         <input type="checkbox" checked={published} onChange={e => setPublished(e.target.checked)} />
                         <span className="slider round"></span>
                      </label>
                   </div>
                </div>
              )}

              {activeTab === 'media' && (
                <div className="tab-pane active animation-fade-in">
                   <div className="visual-studio">
                      <div className="preview-main-box supplier">
                         {previewUrl ? <img src={previewUrl} alt="Preview" /> : <div className="no-image"><Building2 size={48}/></div>}
                      </div>
                      <div className="visual-actions">
                         <label className="btn-upload-premium">
                            <Camera size={18}/> <span>Changer le logo</span>
                            <input type="file" hidden accept="image/*" onChange={handleLogoChange} />
                         </label>
                         {logo && <button type="button" className="btn-remove-media" onClick={() => setLogo(null)}><Trash2 size={16}/> Supprimer</button>}
                         <div className="visual-notes">
                            <Info size={14}/> <span>Format horizontal sur fond blanc recommandé.</span>
                         </div>
                      </div>
                   </div>
                </div>
              )}
           </div>

           <footer className="modal-footer-premium">
              <button type="button" className="btn-modal-cancel" onClick={onClose}>Annuler</button>
              <button type="submit" className="btn-modal-save" disabled={saving}>
                 {saving ? <RefreshCcw size={18} className="spin"/> : <Save size={18}/>}
                 <span>{mode === 'create' ? 'Ajouter la référence' : 'Mettre à jour'}</span>
              </button>
           </footer>
        </form>
      </div>
    </div>
  );
}
